import { useMediaQuery, useTheme } from '@mui/material';

import { getComponentMapByTheme } from './componentMap';

import { useAppContext } from '@shared-components-web/context/AppContextProvider.ctx';
import { TDisplayScreen } from '@shared-components-web/types';
import { PageContentsItem } from '@src/lib/__generated/contentful/sdk';
import { getLogger } from '@src/lib/logging/logger';

export type ComponentProps = PageContentsItem & {
	display?: TDisplayScreen;
	variant?: string;
};

interface Props {
	componentProps: ComponentProps;
	typename: string;
	tags?: Array<string>;
	[key: string]: unknown;
}

const logger = getLogger('design-system');

const canDisplayContentOnScreen = (isSmallScreen: boolean, displayScreen: TDisplayScreen = 'Both') => {
	switch (displayScreen) {
		case 'Desktop':
			return !isSmallScreen;
		case 'Mobile':
			return isSmallScreen;
		case 'Hide':
			return false;
		case 'Both':
		default:
			return true;
	}
};

export const ComponentResolver = ({ typename, componentProps, tags, ...restProps }: Props) => {
	const { variant } = componentProps;
	const { config } = useAppContext();
	const { themeId } = config;
	const componentsMap = getComponentMapByTheme(themeId, variant || '');
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const shouldDisplayContent = canDisplayContentOnScreen(isSmallScreen, componentProps?.display);

	if (!shouldDisplayContent) {
		logger.info(`Not displaying ${typename} for current screen size`);
		return null;
	}

	const Component = componentsMap[typename as keyof typeof componentsMap] as
		| React.ComponentType<Record<string, unknown>>
		| undefined;

	if (!Component) {
		logger.info(`No Template Component for ${typename}`);
		return null;
	}

	logger.info(`Created ${typename} ${componentProps?.sys?.id ?? ''}`);

	return <Component {...(componentProps as Record<string, unknown>)} {...restProps} tags={tags} variant={variant} />;
};
